import classNames from "classnames";
import { FC, Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useI18n from "../../hooks/useTranslations";
import { useJiraApi } from "../../http/jira/api";
import { Worklog } from "../../http/jira/types";
import { downloadFileFromData, formatDate, formatTimeStamp, isHoliday, toIsoStringShort } from "../../utils";
import SvgRefresh from "../icons/Refresh";
import Button from "../ui/Button";
import Header from "../ui/Header";
import LoadingSpinner from "../ui/LoadingSpinner";
import ModalDialog from "../ui/ModalDialog";
import Table from "../ui/Table";
import styles from "./WeeklyReport.module.css";

type tableRow = {
  user: string;
  worklogs?: Map<string, Worklog[]>;
};

const WeeklyReport: FC = () => {
  const i18n = useI18n();
  const navigate = useNavigate();
  const jiraApi = useJiraApi();
  const [status, setStatus] = useState<{
    currentDate: string;
    isLoading: boolean;
    isError: boolean;
    issues: tableRow[];
  }>({ currentDate: toIsoStringShort(getLastMonday()), isLoading: false, isError: false, issues: [] });
  const [worklog, setWorklog] = useState<Worklog[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const goToWeek = (week: string) => {
    const params = new URLSearchParams(location.search);
    params.set("page", `${week}`);
    navigate({ search: params.toString() });
  };

  const dates = getWeekDates(new Date(status.currentDate));

  const loadData = async (date: string) => {
    setStatus((prev) => ({
      ...prev,
      currentDate: date,
      isLoading: true,
    }));
    try {
      const to = new Date(date);
      to.setDate(to.getDate() + 6);

      const worklogs = await jiraApi.useWorklogs(date, to.toISOString());

      const grouped = worklogs.reduce((acc, worklog) => {
        const { author, time } = worklog;
        const date = toIsoStringShort(new Date(time));

        let userWorklogs = acc.get(author.name);
        if (!userWorklogs) {
          userWorklogs = new Map<string, Worklog[]>();
          acc.set(author.name, userWorklogs);
        }

        let logs = userWorklogs.get(date);
        if (!logs) {
          logs = [];
          userWorklogs.set(date, logs);
        }

        logs.push(worklog);

        return acc;
      }, new Map<string, Map<string, Worklog[]>>());

      // Convert the grouped map to an array of objects
      const issues = grouped
        ? [...grouped.entries()].map(([user, worklogs]) => ({
            user,
            worklogs,
          }))
        : [];

      setStatus((prev) => ({
        ...prev,
        isLoading: false,
        issues: issues,
      }));
    } catch (err) {
      setStatus((prev) => ({
        ...prev,
        isLoading: false,
        isError: true,
        issues: [],
      }));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get("page");
    loadData(page || status.currentDate);
  }, [location.search]);

  return (
    <>
      <Header
        title={i18n.translation.common.weeklyReport}
        buttons={
          <Button
            glyph={SvgRefresh}
            buttonProps={{
              onClick: () => {
                jiraApi.invalidateWorklogsCache();
                loadData(status.currentDate);
              },
            }}
          ></Button>
        }
      />
      <Table<tableRow>
        buttons={{
          main: [
            <Button
              buttonProps={{
                onClick: async () => {
                  setIsExporting(true);
                  try {
                    const to = new Date(status.currentDate);
                    to.setDate(to.getDate() + 6);
                    const r = await jiraApi.exportExcel(status.currentDate, to.toISOString());
                    r && downloadFileFromData(r?.data, "jira_worklogs", r?.contentType);
                  } catch (err) {
                    console.log(err);
                  } finally {
                    setIsExporting(false);
                  }
                },
              }}
            >
              {i18n.translation.common.exportExcel}
            </Button>,
            isExporting ? <LoadingSpinner /> : undefined,
          ],
        }}
        head={
          <tr>
            <th>{i18n.translation.common.author}</th>
            {dates.map((d) => (
              <th align="left" key={d.toLocaleDateString()}>
                {formatDate(d.toISOString())}
              </th>
            ))}
          </tr>
        }
        renderRow={(item) => (
          <>
            <td>{item.user}</td>
            {dates.map((d) => {
              const date = toIsoStringShort(d);
              const wl = item.worklogs?.get(date);
              const seconds = wl?.reduce((rv: number, v) => rv + v.timeSpentSeconds, 0);
              const hours = seconds ? seconds / 3600 : 0;
              return (
                <Fragment key={item.user + date}>
                  <td
                    onClick={() => wl && hours && setWorklog(wl)}
                    className={classNames({
                      [styles.red]: !isHoliday(toIsoStringShort(d)) && d.getDay() != 0 && d.getDay() != 6 && hours < 6,
                      [styles.weekend]: d.getDay() == 0 || d.getDay() == 6,
                      [styles.holiday]: isHoliday(toIsoStringShort(d)),
                      [styles.clickable]: !!hours,
                    })}
                  >
                    {hours.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </Fragment>
              );
            })}
          </>
        )}
        error={status.isError}
        isLoading={status.isLoading}
        items={status.issues}
        forcePagination={{
          prevPage: {
            action: () => {
              const d = new Date(status.currentDate);
              d.setDate(d.getDate() - 7);
              goToWeek(toIsoStringShort(d));
            },
            title: i18n.translation.common.prevPage,
          },
          nextPage: {
            action: () => {
              const d = new Date(status.currentDate);
              d.setDate(d.getDate() + 7);
              goToWeek(toIsoStringShort(d));
            },
            title: i18n.translation.common.nextPage,
          },
        }}
      />
      <ModalDialog isOpen={!!worklog.length} onClose={() => setWorklog([])} title={i18n.translation.common.worklogs}>
        <Table<Worklog>
          head={
            <tr>
              <th align="left">{i18n.translation.common.issue}</th>
              <th align="left">{i18n.translation.common.date}</th>
              <th align="left">{i18n.translation.common.time}</th>
            </tr>
          }
          renderRow={(item) => (
            <>
              <td>{item.issue.title}</td>
              <td>{formatTimeStamp(item.time)}</td>
              <td>
                {(item.timeSpentSeconds / 3600).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}{" "}
                h
              </td>
            </>
          )}
          items={worklog.sort((a, b) => {
            if (!a.time) return -1;
            if (!b.time) return 1;
            return a.time.localeCompare(b.time);
          })}
        />
      </ModalDialog>
    </>
  );
};

export default WeeklyReport;

const getLastMonday = (): Date => {
  const date = new Date();
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysToLastMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust if today is Sunday
  const lastMonday = new Date(date);
  lastMonday.setDate(date.getDate() - daysToLastMonday);
  lastMonday.setHours(0, 0, 0, 0); // Reset time
  return lastMonday;
};

const getWeekDates = (startDate: Date) =>
  Array.from({ length: 7 }, (_, i) => {
    const day = new Date(startDate);
    day.setDate(startDate.getDate() + i);
    return day;
  });
